import { auth } from '@mesa/core/auth/better-auth';
import { createServerFn } from '@tanstack/react-start';
import { getRequestHeaders, setResponseHeader } from '@tanstack/react-start/server';
import { APIError } from 'better-auth/api';
import { Resource } from 'sst';

function forwardCookies(responseHeaders: Headers) {
  const cookies = responseHeaders.getSetCookie();
  if (cookies.length > 0) {
    setResponseHeader('Set-Cookie', cookies);
  }
}

function verificationCallbackURL(redirect?: string) {
  const url = new URL('/sign-in', Resource.Config.APP_URL);
  url.searchParams.set('verified', 'true');
  if (redirect) url.searchParams.set('redirect', redirect);
  return url.toString();
}

// Auth bootstrap functions — these bypass baseAuthMiddleware because they ARE the auth
// resolution layer. Auth validation happens in Better Auth via credentials / session cookie.

export const signInServerFn = createServerFn({ method: 'POST' })
  .validator((data: { email: string; password: string; redirect?: string }) => data)
  .handler(async ({ data }) => {
    const headers = getRequestHeaders();
    try {
      const { headers: responseHeaders, response } = await auth.api.signInEmail({
        headers,
        body: { email: data.email, password: data.password, callbackURL: verificationCallbackURL(data.redirect) },
        returnHeaders: true,
      });
      forwardCookies(responseHeaders);
      return { verificationRequired: false, ...response };
    } catch (error) {
      if (error instanceof APIError && error.body?.code === 'EMAIL_NOT_VERIFIED') {
        return { verificationRequired: true };
      }
      throw error;
    }
  });

export const signUpServerFn = createServerFn({ method: 'POST' })
  .validator((data: { email: string; password: string; name: string; redirect?: string }) => data)
  .handler(async ({ data }) => {
    const headers = getRequestHeaders();
    const { headers: responseHeaders, response } = await auth.api.signUpEmail({
      headers,
      body: {
        email: data.email,
        password: data.password,
        name: data.name,
        callbackURL: verificationCallbackURL(data.redirect),
      },
      returnHeaders: true,
    });
    forwardCookies(responseHeaders);
    return response;
  });

export const sendVerificationEmailServerFn = createServerFn({ method: 'POST' })
  .validator((data: { email: string; redirect?: string }) => data)
  .handler(async ({ data }) => {
    await auth.api.sendVerificationEmail({
      headers: getRequestHeaders(),
      body: { email: data.email, callbackURL: verificationCallbackURL(data.redirect) },
    });
  });

export const signOutServerFn = createServerFn({ method: 'POST' }).handler(async () => {
  const headers = getRequestHeaders();
  const { headers: responseHeaders } = await auth.api.signOut({
    headers,
    returnHeaders: true,
  });
  forwardCookies(responseHeaders);
});

export const requestPasswordResetServerFn = createServerFn({ method: 'POST' })
  .validator((data: { email: string }) => data)
  .handler(async ({ data }) => {
    const headers = getRequestHeaders();
    const appUrl = Resource.Config.APP_URL;
    await auth.api.requestPasswordReset({
      headers,
      body: {
        email: data.email,
        redirectTo: `${appUrl}/reset-password`,
      },
    });
  });

export const resetPasswordServerFn = createServerFn({ method: 'POST' })
  .validator((data: { newPassword: string; token: string }) => data)
  .handler(async ({ data }) => {
    const headers = getRequestHeaders();
    await auth.api.resetPassword({
      headers,
      body: { newPassword: data.newPassword, token: data.token },
    });
  });

export const getSessionServerFn = createServerFn({ method: 'GET' }).handler(async () => {
  const headers = getRequestHeaders();
  return auth.api.getSession({ headers });
});
